import {
  getPowerBySlug,
  resolveApiFilterCategory,
  type PowerPrice,
} from '@/lib/powers-data';
import { XAT_API } from '@/services/api-paths';

export type PowerMentionData = {
  slug: string;
  name: string;
  description: { short: string; long: string };
  category: string;
  type?: string;
  categories: string[];
  limited: boolean;
  status: false | string;
  released: string;
  prices: PowerPrice;
  smilies: string[];
};

type CacheEntry = {
  promise: Promise<PowerMentionData | null>;
};

const mentionCache = new Map<string, CacheEntry>();

function parseReleased(raw: string | undefined): string {
  if (!raw?.trim()) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw.trim();
  return d.toISOString().slice(0, 10);
}

function isLimited(raw: Record<string, unknown>): boolean {
  const cats = raw.categories as string[] | undefined;
  if (cats?.includes('is_limited')) return true;
  if (raw.limited === true) return true;
  return false;
}

function resolveCategory(raw: Record<string, unknown>): string {
  const apiType = typeof raw.type === 'string' ? raw.type : '';
  const apiCategories = (raw.categories as string[]) ?? [];
  if (apiType || apiCategories.length > 0) {
    return resolveApiFilterCategory(apiType, apiCategories);
  }
  return 'smiley';
}

function resolveMentionCategory(raw: Record<string, unknown>, slug: string): string {
  const key = slug.toLowerCase();
  if (key === 'everypower') return 'epic';

  const fromApi = resolveCategory(raw);
  const mock = getPowerBySlug(key);
  // API costuma omitir `type` (ex.: nameglow) — mesmo fallback das páginas de power.
  return mock?.category || fromApi;
}

function parsePowerMentionFromApi(raw: Record<string, unknown>, slug: string): PowerMentionData {
  const prices = (raw.prices as PowerPrice) ?? { fairtrade_min: 0, fairtrade_max: 0, store: '0' };
  const description = (raw.description as { long: string; short: string }) ?? {
    long: '',
    short: '',
  };
  const key = slug.toLowerCase();
  const mock = getPowerBySlug(key);

  return {
    slug: key,
    name: (raw.name as string) ?? mock?.name ?? key,
    description,
    prices,
    status: (raw.status as false | string) ?? false,
    released: parseReleased(typeof raw.released === 'string' ? raw.released : undefined),
    categories: (raw.categories as string[]) ?? [],
    type: mock?.type ?? (typeof raw.type === 'string' ? raw.type : undefined),
    category: resolveMentionCategory(raw, key),
    limited: isLimited(raw),
    smilies: Array.isArray(raw.smilies) ? raw.smilies.map(String) : [],
  };
}

async function fetchPowerMentionFromApi(slug: string): Promise<PowerMentionData | null> {
  const key = slug.trim().toLowerCase();
  if (!key) return null;

  try {
    const res = await fetch(XAT_API.powerinfo(key), { cache: 'no-store' });
    if (!res.ok) return null;

    const data = await res.json();
    const raw =
      data?.code === 200 && data.power
        ? data.power
        : data?.code === 200 && Array.isArray(data.powers) && data.powers.length > 0
          ? data.powers[0]
          : null;

    if (!raw || typeof raw !== 'object') return null;
    return parsePowerMentionFromApi(raw as Record<string, unknown>, key);
  } catch {
    return null;
  }
}

/** Busca dados do preview com cache em memória (uma requisição por slug por sessão). */
export function getPowerMentionInfo(slug: string): Promise<PowerMentionData | null> {
  const key = slug.trim().toLowerCase();
  if (!key) return Promise.resolve(null);

  const cached = mentionCache.get(key);
  if (cached) return cached.promise;

  const promise = fetchPowerMentionFromApi(key);
  mentionCache.set(key, { promise });
  return promise;
}

export function formatSlugAsName(slug: string): string {
  const s = slug.trim();
  if (!s) return '';
  return formatPowerDisplayName(s);
}

/** Nome legível em minúsculas (convenção xat, ex.: nameglow). */
export function formatPowerDisplayName(name: string): string {
  const t = name.trim();
  if (!t) return '';
  return t.toLowerCase();
}

/** Smiley principal — mesmo critério das páginas de power (slug). */
export function getPowerMentionSmileyCode(data: PowerMentionData): string {
  return data.slug;
}
